import { SiteMapScreen } from '../screens/site-map/SiteMap.js'
import { lazy } from 'react'
import { Navigate, Route, Routes } from 'react-router'
import { Shell } from '../shell/Shell.js'

// Every screen loads lazily: the shell (sidebar, header, command palette) is
// the only thing every authenticated session needs immediately, and the
// screen the user actually lands on next is unpredictable — so there is no
// "first screen" worth bundling eagerly the way Sign-in is (see App.tsx).
// The editor is the heaviest (FLIP, live preview, the slash palette) and the
// least often the first page loaded, which is exactly the case this is for.
// Shell.tsx wraps the <Outlet> these render into with the <Suspense>
// boundary, so the sidebar stays put while a route's code loads.
const Home = lazy(() => import('../screens/home/Home.js').then((m) => ({ default: m.Home })))
const ContentBrowser = lazy(() =>
  import('../screens/content/ContentBrowser.js').then((m) => ({ default: m.ContentBrowser })),
)
const EntryEditor = lazy(() => import('../screens/editor/EntryEditor.js').then((m) => ({ default: m.EntryEditor })))
const ContentModel = lazy(() => import('../screens/model/ContentModel.js').then((m) => ({ default: m.ContentModel })))
const Settings = lazy(() => import('../screens/settings/Settings.js').then((m) => ({ default: m.Settings })))
const Media = lazy(() => import('../screens/media/Media.js').then((m) => ({ default: m.Media })))
const Inbox = lazy(() => import('../screens/inbox/Inbox.js').then((m) => ({ default: m.Inbox })))
const Schedule = lazy(() => import('../screens/schedule/Schedule.js').then((m) => ({ default: m.Schedule })))
const Forms = lazy(() => import('../screens/forms/Forms.js').then((m) => ({ default: m.Forms })))
const FormDetail = lazy(() => import('../screens/forms/FormDetail.js').then((m) => ({ default: m.FormDetail })))
const Updates = lazy(() => import('../screens/updates/Updates.js').then((m) => ({ default: m.Updates })))
const VersionHistory = lazy(() =>
  import('../screens/history/VersionHistory.js').then((m) => ({ default: m.VersionHistory })),
)
const CustomBlocks = lazy(() => import('../screens/model/CustomBlocks.js').then((m) => ({ default: m.CustomBlocks })))
const CustomBlockDetail = lazy(() => import('../screens/model/CustomBlockDetail.js').then((m) => ({ default: m.CustomBlockDetail })))
const Setup = lazy(() => import('../screens/setup/Setup.js').then((m) => ({ default: m.Setup })))
const ReviewQueue = lazy(() => import('../screens/review/ReviewQueue.js').then((m) => ({ default: m.ReviewQueue })))
const ReviewEntry = lazy(() => import('../screens/review/ReviewEntry.js').then((m) => ({ default: m.ReviewEntry })))
const Account = lazy(() => import('../screens/account/Account.js').then((m) => ({ default: m.Account })))
const SectionScreen = lazy(() => import('../screens/sections/SectionScreen.js').then((m) => ({ default: m.SectionScreen })))
const AlreadySignedIn = lazy(() => import('../screens/signin/AlreadySignedIn.js').then((m) => ({ default: m.AlreadySignedIn })))

export function AppRoutes() {
  return (
    <Routes>
      {/* Signed-out links opened with a live session: a panel, not a silent redirect to Home. The session stays until they sign out. */}
      <Route path="accept-invite" element={<AlreadySignedIn />} />
      <Route path="reset-password" element={<AlreadySignedIn />} />
      <Route element={<Shell />}>
        <Route index element={<Home />} />
        <Route path="content" element={<ContentBrowser />} />
        <Route path="content/:id" element={<EntryEditor />} />
        <Route path="content/:id/history" element={<VersionHistory />} />
        <Route path="site-map" element={<SiteMapScreen />} />
        <Route path="sections/:key" element={<SectionScreen />} />
        <Route path="media" element={<Media />} />
        <Route path="model" element={<ContentModel />} />
        <Route path="model/blocks" element={<CustomBlocks />} />
        <Route path="model/blocks/:key" element={<CustomBlockDetail />} />
        <Route path="inbox" element={<Inbox />} />
        <Route path="schedule" element={<Schedule />} />
        <Route path="forms" element={<Forms />} />
        <Route path="forms/:id" element={<FormDetail />} />
        <Route path="settings/:page?" element={<Settings />} />
        <Route path="updates" element={<Updates />} />
        <Route path="setup" element={<Setup />} />
        <Route path="review" element={<ReviewQueue />} />
        <Route path="review/:id" element={<ReviewEntry />} />
        <Route path="account" element={<Account />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  )
}
